import { useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Loader2,
  Phone,
  Search,
} from "lucide-react";

import {
  useEccCallDownload,
  useEccCallJob,
  useEccCallSearch,
} from "@/hooks/useEcc";
import { toLocalIsoDate } from "@/lib/localDate";

const SHORTCUTS = [
  { label: "Hoy", days: 0 },
  { label: "Ayer", days: 1 },
  { label: "Últimos 7 días", days: 7 },
  { label: "Últimos 15 días", days: 15 },
  { label: "Últimos 30 días", days: 30 },
];

function toIsoDate(d: Date) {
  // Fecha LOCAL (no UTC). Ver lib/localDate: toISOString() rodaría a mañana
  // después de las 18:00 CST y "Hoy" consultaría un día sin llamadas.
  return toLocalIsoDate(d);
}

function todayIso() {
  return toIsoDate(new Date());
}

function daysAgoIso(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toIsoDate(d);
}

function fmtDuration(seconds: number) {
  const s = Math.max(0, Math.floor(seconds));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${String(ss).padStart(2, "0")}`;
}

// calldate viene como "YYYY-MM-DD HH:MM:SS" en hora local del cliente
// (Tilatina). Se muestra recortado a minutos, sin reinterpretar zona horaria.
function fmtCallDate(calldate: string) {
  return calldate.slice(0, 16);
}

// TODAS las llamadas con `recordingfile` son descargables, contestadas o no.
// Antes se creía que el WAV de una NO ANSWER nunca se escribía y la casilla
// estaba deshabilitada; comprobado contra producción el 2026-08-12, es falso:
// una no contestada del 10-ago bajó 269 KB desde media1 y una de ese mismo día
// bajó desde el conmutador. El diagnóstico original se hizo sobre llamadas del
// día en curso, que todavía no estaban en media1 —copyAsteriskFiles.py las
// mueve a la 1 AM— y se leyó "no existe" donde era "todavía no llega".
// El audio de una no contestada es el timbrado, y sirve: es la evidencia de
// cuánto timbró antes de entrar el buzón.
const DISPOSITION_LABELS: Record<string, string> = {
  ANSWERED: "Contestada",
  "NO ANSWER": "No contestada",
  BUSY: "Ocupada",
  FAILED: "Fallida",
};

function dispositionLabel(disposition: string) {
  return DISPOSITION_LABELS[disposition] ?? disposition;
}

// Etiquetas en español para las razones que clasifica el fetcher
// (classify_download_error). Resumen los fallos por-llamada que quedan en
// job.log_lines tras una descarga, para mostrarlos al operador.
const FAILURE_REASON_LABELS: Record<string, string> = {
  file_not_found: "grabación no encontrada en el servidor",
  auth_failed: "error de autenticación",
  host_key_issue: "problema de host key",
  connection_failed: "sin conexión al servidor",
  dns_failed: "error de DNS",
  timeout: "tiempo de espera agotado",
  other: "error de descarga",
};

function summarizeDownloadFailures(logLines: string[]) {
  // OJO: job.log_lines se ACUMULA entre reintentos sobre la misma fila del job
  // (cada POST de descarga reusa el job_id y _job_append_log agrega, no
  // reemplaza). Un job que terminó OK puede arrastrar líneas `Fallo descarga`
  // de intentos previos. Para no contar fallos viejos nos quedamos solo con la
  // cola desde el último marcador "Descargando N llamada(s)…".
  let start = 0;
  for (let i = logLines.length - 1; i >= 0; i--) {
    if (/Descargando \d+ llamada/i.test(logLines[i])) {
      start = i;
      break;
    }
  }
  const failures = logLines
    .slice(start)
    .filter((l) => /Fallo descarga/i.test(l));
  const byReason: Record<string, number> = {};
  for (const line of failures) {
    const m = line.match(/\(([a-z_]+)\)/);
    const reason = (m && m[1]) || "other";
    byReason[reason] = (byReason[reason] ?? 0) + 1;
  }
  const detail = Object.entries(byReason)
    .map(([r, n]) => `${FAILURE_REASON_LABELS[r] ?? r} (${n})`)
    .join(" · ");
  return { count: failures.length, detail };
}

export function EccCallsPage() {
  const [number, setNumber] = useState("");
  const [periodStart, setPeriodStart] = useState(daysAgoIso(7));
  const [periodEnd, setPeriodEnd] = useState(todayIso());
  const [jobId, setJobId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Transcribir es opcional y cuesta minutos por llamada (Whisper). Nace
  // marcado para que quien no se fije reciba lo mismo que recibía antes.
  const [transcribe, setTranscribe] = useState(true);
  // Modo con el que se DISPARÓ la descarga en curso. La casilla se puede
  // mover cuando el ZIP ya está listo; si el avance y la entrega leyeran la
  // casilla viva, la tarjeta prometería un .txt que el ZIP no trae.
  const [modoEntregado, setModoEntregado] = useState(true);

  const search = useEccCallSearch();
  const { data: job } = useEccCallJob(jobId);
  const download = useEccCallDownload(jobId ?? "");

  const numberTrimmed = number.trim();
  const canSearch = numberTrimmed.length >= 2 && periodStart <= periodEnd;
  const queued = job?.status === "queued";
  const hasResults = (job?.results?.length ?? 0) > 0;
  // "En cola" se muestra como el trabajo que le sigue: búsqueda si aún no hay
  // resultados, o descarga si ya los hay.
  const searching =
    search.isPending || job?.status === "searching" || (queued && !hasResults);
  const downloading =
    download.isPending || job?.status === "downloading" || (queued && hasResults);

  const results = useMemo(() => job?.results ?? [], [job]);
  // Toda llamada que el CDR reporta con grabación es descargable.
  const downloadable = useMemo(
    () => results.filter((r) => r.recordingfile),
    [results],
  );
  const allSelected =
    downloadable.length > 0 &&
    downloadable.every((r) => selected.has(r.uniqueid));
  const downloadFailures = useMemo(
    () => summarizeDownloadFailures(job?.log_lines ?? []),
    [job],
  );

  function applyShortcut(days: number) {
    setPeriodStart(daysAgoIso(days));
    setPeriodEnd(todayIso());
  }

  function handleSearch() {
    if (!canSearch) return;
    setSelected(new Set());
    search.mutate(
      { number: numberTrimmed, period_start: periodStart, period_end: periodEnd },
      { onSuccess: (ref) => setJobId(ref.job_id) },
    );
  }

  function toggleOne(uniqueid: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uniqueid)) next.delete(uniqueid);
      else next.add(uniqueid);
      return next;
    });
  }

  function toggleAll() {
    setSelected((prev) => {
      const allOn =
        downloadable.length > 0 &&
        downloadable.every((r) => prev.has(r.uniqueid));
      return allOn ? new Set() : new Set(downloadable.map((r) => r.uniqueid));
    });
  }

  function handleDownload() {
    if (selected.size === 0 || !jobId) return;
    setModoEntregado(transcribe);
    download.mutate({ uniqueids: [...selected], transcribe });
  }

  const lastLog = job?.log_lines?.[job.log_lines.length - 1];
  // El botón de descarga se puede usar cuando hay resultados y no hay un
  // trabajo en curso (results_ready, o re-descargar tras done/failed).
  const canDownload =
    !!job &&
    ["results_ready", "done", "failed"].includes(job.status) &&
    selected.size > 0 &&
    !downloading;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Buscar / descargar llamada</h2>
        <p className="text-sm text-muted-foreground">
          Busca una llamada por teléfono o extensión (entrada o salida), elige
          cuáles bajar y descarga un ZIP con el audio y —si lo marcas— su
          transcripción.
        </p>
      </div>

      {/* ── Buscador ───────────────────────────────────────────────── */}
      <div className="rounded-lg border bg-card p-4 space-y-4">
        <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor="call-number">
              Teléfono o extensión
            </label>
            <div className="relative">
              <Phone className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <input
                id="call-number"
                type="text"
                inputMode="tel"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="749  ó  5512345678"
                className="w-full rounded-md border bg-background py-2 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={handleSearch}
              disabled={!canSearch || searching}
              className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {searching ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Search className="size-4" />
              )}
              Buscar
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor="call-start">
              Desde
            </label>
            <input
              id="call-start"
              type="date"
              value={periodStart}
              max={periodEnd}
              onChange={(e) => setPeriodStart(e.target.value)}
              className="rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor="call-end">
              Hasta
            </label>
            <input
              id="call-end"
              type="date"
              value={periodEnd}
              min={periodStart}
              max={todayIso()}
              onChange={(e) => setPeriodEnd(e.target.value)}
              className="rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {SHORTCUTS.map((s) => (
              <button
                key={s.label}
                type="button"
                onClick={() => applyShortcut(s.days)}
                className="rounded-md border px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {search.isError && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="size-4 shrink-0" />
          No se pudo iniciar la búsqueda. Verifica el número y el rango.
        </div>
      )}

      {/* ── Resultados ─────────────────────────────────────────────── */}
      {searching && (
        <div className="flex items-center gap-2 rounded-md border bg-card p-4 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {queued
            ? "En cola · esperando turno…"
            : `Buscando llamadas de ${numberTrimmed} en el CDR…`}
        </div>
      )}

      {job && !searching && (
        <div className="rounded-lg border bg-card">
          <div className="flex items-center justify-between border-b p-3">
            <div className="text-sm font-medium">
              {results.length} llamada(s) encontrada(s)
              {downloadable.length < results.length && (
                <span className="ml-2 font-normal text-muted-foreground">
                  · {downloadable.length} con grabación
                </span>
              )}
              {selected.size > 0 && (
                <span className="ml-2 font-normal text-muted-foreground">
                  · {selected.size} seleccionada(s)
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {/* La transcripción es lo caro del trabajo: minutos por llamada
                  contra segundos de descarga. Quien sólo quiere oír la llamada
                  apaga esto y el ZIP baja con puro .wav. */}
              <label
                className="flex cursor-pointer select-none items-center gap-2 text-sm text-muted-foreground"
                title="Transcribir tarda varios minutos por llamada. Sin esto el ZIP trae sólo el .wav."
              >
                <input
                  type="checkbox"
                  checked={transcribe}
                  onChange={(e) => setTranscribe(e.target.checked)}
                  disabled={downloading}
                  className="disabled:cursor-not-allowed disabled:opacity-40"
                />
                Incluir transcripción
              </label>
              <button
                type="button"
                onClick={handleDownload}
                disabled={!canDownload}
                className="flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                {downloading ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Download className="size-4" />
                )}
                Descargar {selected.size > 0 ? `(${selected.size})` : ""}
              </button>
            </div>
          </div>

          {results.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              Sin llamadas con grabación para ese número en el rango elegido.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr className="border-b">
                  <th className="w-10 p-2 text-center">
                    <input
                      type="checkbox"
                      aria-label="Seleccionar todas"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={downloadable.length === 0}
                      className="disabled:cursor-not-allowed disabled:opacity-40"
                    />
                  </th>
                  <th className="p-2 text-left font-medium">Fecha y hora</th>
                  <th className="p-2 text-left font-medium">Origen</th>
                  <th className="p-2 text-left font-medium">Destino</th>
                  <th className="p-2 text-right font-medium">Duración</th>
                  <th className="p-2 text-left font-medium">Estado</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => {
                  const downloadableRow = Boolean(r.recordingfile);
                  return (
                    <tr
                      key={r.uniqueid}
                      className={`border-b last:border-0 hover:bg-accent/50 ${
                        downloadableRow ? "" : "text-muted-foreground"
                      }`}
                    >
                      <td className="p-2 text-center">
                        <input
                          type="checkbox"
                          aria-label={`Seleccionar ${r.uniqueid}`}
                          checked={selected.has(r.uniqueid)}
                          onChange={() => toggleOne(r.uniqueid)}
                          disabled={!downloadableRow}
                          title={
                            downloadableRow
                              ? undefined
                              : "El conmutador no registró grabación para esta llamada."
                          }
                          className="disabled:cursor-not-allowed disabled:opacity-40"
                        />
                      </td>
                      <td className="p-2 tabular-nums">
                        {fmtCallDate(r.calldate)}
                      </td>
                      <td className="p-2">{r.src || "—"}</td>
                      <td className="p-2">{r.dst || "—"}</td>
                      <td className="p-2 text-right tabular-nums">
                        {fmtDuration(r.duration)}
                      </td>
                      <td className="p-2 text-muted-foreground">
                        {dispositionLabel(r.disposition)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {downloadable.length < results.length && (
            <div className="border-t px-3 py-2 text-xs text-muted-foreground">
              Las llamadas sin grabación en el conmutador no se pueden
              descargar. El resto sí, hayan sido contestadas o no: en una no
              contestada el audio es el timbrado.
            </div>
          )}
        </div>
      )}

      {/* ── Progreso / entrega ─────────────────────────────────────── */}
      {downloading && (
        <div className="flex items-center gap-2 rounded-md border bg-card p-4 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {queued
            ? "En cola · esperando turno…"
            : (lastLog ??
              (modoEntregado
                ? "Descargando y transcribiendo las llamadas seleccionadas…"
                : "Descargando las llamadas seleccionadas…"))}
        </div>
      )}

      {job?.status === "done" && job.has_output && (
        <a
          href={`/api/v1/ecc/calls/${job.id}/file`}
          className="flex items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 transition-colors hover:bg-emerald-500/15"
        >
          <CheckCircle2 className="size-6 shrink-0 text-emerald-600" />
          <div className="flex-1">
            <div className="text-sm font-medium">ZIP listo para descargar</div>
            <div className="text-xs text-muted-foreground">
              {modoEntregado
                ? "Audio (.wav) + transcripción (.txt) de cada llamada seleccionada."
                : "Audio (.wav) de cada llamada seleccionada."}
            </div>
          </div>
          <Download className="size-5 text-emerald-600" />
        </a>
      )}

      {/* Éxito parcial: el ZIP salió pero alguna llamada falló. Sin esto el
          fallo desaparecía (status=done con ZIP). */}
      {job?.status === "done" && downloadFailures.count > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700">
          <AlertTriangle className="size-4 shrink-0" />
          <div>
            <div className="font-medium">
              {downloadFailures.count} llamada(s) no se pudieron descargar
            </div>
            <div className="text-xs opacity-90">{downloadFailures.detail}</div>
          </div>
        </div>
      )}

      {job?.status === "failed" && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="size-4 shrink-0" />
          <div>
            <div className="font-medium">No se pudo completar la descarga.</div>
            {downloadFailures.count > 0 ? (
              <div className="text-xs opacity-90">
                {downloadFailures.count} llamada(s): {downloadFailures.detail}
              </div>
            ) : (
              job.error && <div className="text-xs opacity-90">{job.error}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
